/** Shared POS receipt printing (POS + POS Sales) */
import { getCurrentUserName, isInvalidPersonName } from './auth';
import { resolveBranchForPrint } from './branchPrint';

/** Cashier on receipts = the user who made the sale, not who is printing now. */
const INVALID_CASHIER_NAMES = new Set([
  'POS User', 'pos user', 'Unknown', 'unknown', 'Cashier', 'cashier',
  'Staff', 'staff', 'Admin', 'admin', 'User', 'user', ''
]);

export const isInvalidCashierName = (name) => {
  const trimmed = String(name || '').trim();
  return !trimmed || INVALID_CASHIER_NAMES.has(trimmed) || isInvalidPersonName(trimmed);
};

export const resolveCashierName = (receiptData = {}) => {
  const candidates = [
    receiptData.user_name,
    receiptData.cashier_name,
    receiptData.cashier?.full_name,
    receiptData.cashier?.name,
    receiptData.cashier?.username
  ];
  for (const name of candidates) {
    const trimmed = String(name || '').trim();
    if (trimmed && !isInvalidCashierName(trimmed)) {
      return trimmed;
    }
  }
  return '';
};

/** Logged-in person's name (e.g. Chamila) — never role like "staff". */
export const getCurrentCashierName = () => getCurrentUserName();

export const parseSerialNumbers = (serialData) => {
  if (!serialData) return [];
  if (Array.isArray(serialData)) {
    return serialData.map(s => String(s).trim()).filter(s => s && s !== '[]');
  }
  if (typeof serialData === 'string') {
    try {
      const parsed = JSON.parse(serialData);
      if (Array.isArray(parsed)) return parsed.map(s => String(s).trim()).filter(Boolean);
      return serialData.trim() ? [serialData.trim()] : [];
    } catch {
      if (serialData.includes(',')) {
        return serialData.split(',').map(s => s.trim()).filter(Boolean);
      }
      return serialData.trim() ? [serialData.trim()] : [];
    }
  }
  return [String(serialData)];
};

export const printReceipt = (receiptData) => {
  const receiptWindow = window.open('', '_blank', 'width=800,height=600,scrollbars=yes,resizable=yes');

  if (!receiptWindow) {
    alert('Please allow pop-ups to print receipts');
    return;
  }

  const processedItems = (receiptData.items || []).map(item => ({
    ...item,
    serial_numbers: parseSerialNumbers(item.serial_numbers)
  }));

  const itemsWithWarranty = processedItems.filter(item => item.warranty_months > 0);
  const isCreditTransaction = receiptData.payment_method === 'credit';
  const isChequeTransaction = receiptData.payment_method === 'cheque';
  const isOnlineTransaction = ['online', 'mobile', 'bank_transfer'].includes(receiptData.payment_method);

  const paymentStatus = receiptData.status ||
    (isChequeTransaction ? 'pending' :
      isCreditTransaction ? (receiptData.status === 'completed' ? 'paid' : 'pending') :
        isOnlineTransaction ? (receiptData.online_payment_status || 'pending') : 'paid');

  const warrantyItemsWithDates = itemsWithWarranty.map(item => {
    const purchaseDate = new Date(receiptData.date || receiptData.transaction_date || Date.now());
    const warrantyEndDate = new Date(purchaseDate);
    warrantyEndDate.setMonth(warrantyEndDate.getMonth() + (item.warranty_months || 0));
    return { ...item, warranty_end_date: warrantyEndDate.toLocaleDateString() };
  });

  const bulkSaleItems = processedItems.filter(item => item.is_bulk_sale);
  const regularItems = processedItems.filter(item => !item.is_bulk_sale);

  const formatPaymentMethod = (method) => {
    switch (method) {
      case 'cash': return 'Cash';
      case 'card': return 'Card';
      case 'bank_transfer': return 'Bank Transfer';
      case 'mobile': return 'Online Payment';
      case 'online': return 'Online Payment';
      case 'credit': return 'Credit';
      case 'cheque': return 'Cheque';
      default: return method?.toUpperCase() || 'CASH';
    }
  };

  const safeNumber = (value) => {
    if (value === null || value === undefined) return 0;
    const num = parseFloat(value);
    return isNaN(num) ? 0 : num;
  };

  const formatNumber = (value) => safeNumber(value).toFixed(2);

  const displayNumber = receiptData.receipt_number || receiptData.pos_number || '';
  const baseUrl = window.location.origin;
  const logoUrl = `${baseUrl}/LOGOFile.png`;
  const receiptDate = receiptData.date || receiptData.transaction_date || new Date().toISOString();
  // Never substitute the person currently viewing/printing — use who created the sale
  const cashierName = resolveCashierName(receiptData) || '—';
  const branch = resolveBranchForPrint({
    branchName: receiptData.branch_name || receiptData.branch?.name,
    branchCode: receiptData.branch_code || receiptData.branch?.branch_code,
    branch: receiptData.branch
  });

  receiptWindow.document.write(`
<!DOCTYPE html>
<html>
<head>
  <title>Invoice #${displayNumber}</title>
  <base href="${baseUrl}/">
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600&family=Roboto+Mono:wght@400;500&display=swap');
    * { margin: 0; padding: 0; box-sizing: border-box; }
    @media print {
      @page { size: A4; margin: 0.5cm; }
      body { margin: 0; padding: 0; }
      .no-print { display: none !important; }
    }
    html, body { height: 100%; margin: 0; padding: 0; }
    body {
      font-family: 'Poppins', sans-serif;
      font-size: 11px;
      padding: 8px;
      max-width: 210mm;
      margin: 0 auto;
      background: white;
      color: #333;
      min-height: 100%;
      display: flex;
      flex-direction: column;
    }
    .receipt-container {
      border: 1px solid #ddd;
      padding: 12px;
      border-radius: 8px;
      flex: 1;
      display: flex;
      flex-direction: column;
    }
    .receipt-content { flex: 1; }
    .header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 12px;
      padding-bottom: 8px;
      border-bottom: 2px solid #1976d2;
    }
    .company-logo { height: 110px; width: auto; max-width: 250px; object-fit: contain; }
    .company-info { flex: 1; text-align: right; }
    .company-name { font-size: 14px; font-weight: 700; color: #1976d2; }
    .store-info { font-size: 11px; color: #666; margin-top: 3px; line-height: 1.3; }
    .invoice-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 10px;
      padding: 6px 10px;
      background: #f5f5f5;
      border-radius: 4px;
    }
    .invoice-title { font-size: 16px; font-weight: 600; color: #1976d2; }
    .invoice-number { font-family: 'Roboto Mono', monospace; font-size: 14px; }
    .status-badge {
      display: inline-flex;
      padding: 3px 8px;
      border-radius: 16px;
      font-size: 11px;
      font-weight: 600;
      margin-left: 8px;
    }
    .status-paid { background: #2e7d32; color: white; }
    .status-pending { background: #f57c00; color: white; }
    .customer-info {
      margin-bottom: 10px;
      padding: 6px 10px;
      background: #f9f9f9;
      border-left: 3px solid #1976d2;
    }
    table { width: 100%; border-collapse: collapse; margin: 8px 0; }
    th { background: #1976d2; color: white; padding: 8px 6px; text-align: left; font-size: 11px; }
    td { padding: 6px; border-bottom: 1px solid #eee; vertical-align: top; }
    .product-name { font-weight: 600; font-size: 12px; margin-bottom: 3px; }
    .product-code { color: #666; font-size: 9px; }
    .discount-text { color: #f57c00; font-size: 11px; margin-top: 2px; }
    .right { text-align: right; }
    .total-row { background: #e3f2fd; font-weight: 700; }
    .warranty-info { font-size: 10px; color: #2e7d32; margin-top: 3px; }
    .serial-info { font-size: 10px; color: #1976d2; margin-top: 3px; font-family: 'Roboto Mono', monospace; }
    .serial-list { margin-top: 2px; }
    .serial-item {
      display: inline-block;
      background: #e3f2fd;
      padding: 2px 6px;
      margin: 2px 4px 2px 0;
      border-radius: 4px;
      font-size: 9px;
      font-family: 'Roboto Mono', monospace;
    }
    .cheque-info, .credit-info {
      padding: 8px 10px;
      border-radius: 4px;
      margin: 8px 0;
      font-size: 10px;
    }
    .cheque-info { background: #e8eaf6; border-left: 3px solid #3f51b5; }
    .credit-info { background: #fff3e0; border-left: 3px solid #f57c00; }
    .footer {
      margin-top: auto;
      text-align: center;
      font-size: 11px;
      color: #666;
      padding-top: 12px;
      border-top: 1px dashed #ddd;
    }
    .signature-section {
      display: flex;
      justify-content: space-between;
      margin: 8px 0 12px;
      padding: 8px;
      font-size: 10px;
    }
    .totals-table { width: 100%; margin-top: 8px; }
    .totals-table td { padding: 4px 6px; border: none; }
    .bundle-items-table { width: 100%; margin-top: 5px; background: white; }
    .bundle-items-table th { font-size: 11px; padding: 4px 5px; }
    .bundle-items-table td { padding: 3px 5px; font-size: 11px; }
    .bundle-total {
      background: #fff;
      padding: 6px 12px;
      border-radius: 4px;
      margin: 6px 0;
      border-left: 3px solid #f57c00;
      display: flex;
      justify-content: space-between;
      font-size: 11px;
    }
    .print-button {
      position: fixed;
      bottom: 20px;
      right: 20px;
      background: #1976d2;
      color: white;
      border: none;
      padding: 10px 20px;
      border-radius: 4px;
      font-size: 13px;
      cursor: pointer;
      font-weight: 600;
      z-index: 1000;
    }
    .print-button:hover { background: #1565c0; }
  </style>
</head>
<body>
  <div class="receipt-container">
    <div class="receipt-content">
      <div class="header">
        <div class="logo-container">
          <img src="${logoUrl}" alt="Company Logo" class="company-logo"
               onerror="this.style.display='none'">
        </div>
        <div class="company-info">
          <div class="company-name">IT Professionals Since 1997</div>
          <div class="store-info">
            1118, Pannipitiya Road, Wickramasinghepura, Thalawathugoda<br>
            Tel: 011 27 96 579 | 94 77 987 1130<br>
            Email: info@sunsystems.lk | Web: sunsystems.lk
          </div>
        </div>
      </div>

      <div class="invoice-header">
        <div>
          <span class="invoice-title">INVOICE</span>
          ${paymentStatus === 'paid' || paymentStatus === 'completed'
            ? '<span class="status-badge status-paid">✓ PAID</span>'
            : '<span class="status-badge status-pending">⌛ PENDING</span>'}
          ${isCreditTransaction ? '<span style="background:#f57c00;color:white;padding:3px 7px;border-radius:3px;margin-left:8px;font-size:9px;">CREDIT</span>' : ''}
          ${isChequeTransaction ? '<span style="background:#3f51b5;color:white;padding:3px 7px;border-radius:3px;margin-left:8px;font-size:9px;">CHEQUE</span>' : ''}
          <div style="margin-top:4px;font-size:10px;color:#1565c0;font-weight:600;">
            Branch: ${branch.label}${branch.code && !branch.label.includes(branch.code) ? ` · ${branch.code}` : ''}
          </div>
        </div>
        <div><span class="invoice-number">#${displayNumber}</span></div>
      </div>

      <div class="customer-info">
        <table style="width:100%;border:none;">
          <tr>
            <td style="border:none;padding:3px;"><strong>Date:</strong> ${new Date(receiptDate).toLocaleDateString()}</td>
            <td style="border:none;padding:3px;"><strong>Time:</strong> ${new Date(receiptDate).toLocaleTimeString()}</td>
          </tr>
          <tr>
            <td style="border:none;padding:3px;"><strong>Customer:</strong> ${receiptData.customer || receiptData.customer_name || 'Walk-in Customer'}</td>
            <td style="border:none;padding:3px;"><strong>Cashier:</strong> ${cashierName}</td>
          </tr>
        </table>
      </div>

      ${isChequeTransaction && receiptData.cheque_details ? `
        <div class="cheque-info">
          <strong>🏦 CHEQUE DETAILS</strong><br>
          <strong>No:</strong> ${receiptData.cheque_details.chequeNumber} |
          <strong>Bank:</strong> ${receiptData.cheque_details.bankName}${receiptData.cheque_details.branch ? ` (${receiptData.cheque_details.branch})` : ''}<br>
          <strong>Cheque Date:</strong> ${new Date(receiptData.cheque_details.chequeDate).toLocaleDateString()} |
          <strong>Due Date:</strong> ${new Date(receiptData.cheque_details.dueDate).toLocaleDateString()}
        </div>
      ` : ''}

      ${isCreditTransaction ? `
        <div class="credit-info">
          <strong>💰 CREDIT SALE</strong><br>
          <strong>Due Date:</strong> ${receiptData.due_date || receiptData.credit_due_date || 'N/A'}
        </div>
      ` : ''}

      <table>
        <thead>
          <tr>
            <th style="width:50%;">Item Description</th>
            <th style="width:10%;">Qty</th>
            <th style="width:20%;" class="right">Unit Price</th>
            <th style="width:20%;" class="right">Total</th>
          </tr>
        </thead>
        <tbody>
          ${regularItems.map(item => {
            const warrantyItem = warrantyItemsWithDates.find(w => w.product_id === item.product_id && w === item) ||
              warrantyItemsWithDates.find(w => w.product_id === item.product_id);
            let warrantyDisplay = '';
            if (warrantyItem && warrantyItem.warranty_months > 0) {
              warrantyDisplay = `<div class="warranty-info">🔰 Warranty: ${warrantyItem.warranty_months} months (until ${warrantyItem.warranty_end_date})</div>`;
            }
            let serialDisplay = '';
            const serials = item.serial_numbers || [];
            if (serials.length > 0) {
              serialDisplay = '<div class="serial-info">🔢 Serial Numbers:<div class="serial-list">';
              serials.forEach(sn => { serialDisplay += `<span class="serial-item">${sn}</span>`; });
              serialDisplay += '</div></div>';
            }
            return `
              <tr>
                <td>
                  <div class="product-name">${item.product_name || item.name}</div>
                  ${item.product_code ? `<div class="product-code">Code: ${item.product_code}</div>` : ''}
                  ${item.discount_percentage > 0 ? `<div class="discount-text">Discount: ${item.discount_percentage}%</div>` : ''}
                  ${warrantyDisplay}
                  ${serialDisplay}
                </td>
                <td style="text-align:center;">${item.quantity}</td>
                <td class="right">${formatNumber(item.unit_price)}</td>
                <td class="right"><strong>${formatNumber(item.line_total)}</strong></td>
              </tr>`;
          }).join('')}

          ${bulkSaleItems.length > 0 ? `
            <tr>
              <td colspan="4" style="padding:8px;">
                <strong style="color:#f57c00;">📦 BUNDLE PACKAGE</strong>
                <table class="bundle-items-table">
                  <thead><tr><th>Items Included</th><th class="right">Qty</th></tr></thead>
                  <tbody>
                    ${bulkSaleItems.map(item => `
                      <tr><td>${item.product_name || item.name}</td><td class="right">${item.quantity}</td></tr>
                    `).join('')}
                  </tbody>
                </table>
                <div class="bundle-total">
                  <span>BUNDLE PACKAGE TOTAL:</span>
                  <span>LKR ${bulkSaleItems.reduce((s, i) => s + safeNumber(i.line_total), 0).toFixed(2)}</span>
                </div>
              </td>
            </tr>
          ` : ''}
        </tbody>
      </table>

      <table class="totals-table">
        <tr>
          <td style="text-align:right;width:80%;"><strong>Subtotal:</strong></td>
          <td style="text-align:right;width:20%;">${formatNumber(receiptData.subtotal)}</td>
        </tr>
        ${safeNumber(receiptData.discount || receiptData.discount_amount) > 0 ? `
          <tr>
            <td style="text-align:right;"><strong>Discount:</strong></td>
            <td style="text-align:right;color:#f57c00;">-${formatNumber(receiptData.discount || receiptData.discount_amount)}</td>
          </tr>
        ` : ''}
        ${safeNumber(receiptData.tax || receiptData.tax_amount) > 0 ? `
          <tr>
            <td style="text-align:right;"><strong>Tax:</strong></td>
            <td style="text-align:right;">${formatNumber(receiptData.tax || receiptData.tax_amount)}</td>
          </tr>
        ` : ''}
        <tr class="total-row">
          <td style="text-align:right;font-size:14px;"><strong>TOTAL:</strong></td>
          <td style="text-align:right;font-size:14px;"><strong>${formatNumber(receiptData.total || receiptData.total_amount)}</strong></td>
        </tr>
        <tr>
          <td style="text-align:right;"><strong>Payment Method:</strong></td>
          <td style="text-align:right;"><strong>${formatPaymentMethod(receiptData.payment_method)}</strong></td>
        </tr>
        ${!isCreditTransaction && !isChequeTransaction && (paymentStatus === 'paid' || paymentStatus === 'completed') ? `
          <tr>
            <td style="text-align:right;"><strong>Amount Paid:</strong></td>
            <td style="text-align:right;">${formatNumber(receiptData.paid || receiptData.amount_paid)}</td>
          </tr>
          <tr>
            <td style="text-align:right;"><strong>Change:</strong></td>
            <td style="text-align:right;color:#2e7d32;">${formatNumber(receiptData.change || receiptData.change_amount)}</td>
          </tr>
        ` : ''}
      </table>
    </div>

    <div class="footer">
      <div class="signature-section">
        <div style="text-align:center;width:45%;">
          ..........................................................<br><strong>Authorize signature</strong><br>${cashierName}
        </div>
        <div style="text-align:center;width:45%;">
          ..........................................................<br><strong>Customer signature</strong>
        </div>
      </div>
      <hr style="border:none;border-top:2px solid #1976d2;margin:8px 0;">
      <p style="font-size:13px;font-weight:600;color:#1976d2;">Thank you for choosing Sun Systems International – We value your trust!</p>
      <p style="font-size:10px;font-weight:700;">Your End-to-End IT Hardware Partner</p>
      <p style="font-size:11px;">• Retail • Repair • Service • Rental • Web Development • System Development • Corporate IT Support • Distribution • Institutional Projects</p>
      <p style="font-size:10px;font-weight:600;">Affordable. Reliable. Guaranteed.</p>
      <hr style="border:none;border-top:1px dashed #ddd;margin:6px 0;">
      <p style="font-size:11px;">ERP System Developed by Sun Systems International | Contact: 94 77 987 1130</p>
    </div>
  </div>
  <button class="print-button no-print" onclick="window.print(); setTimeout(() => window.close(), 1000);">🖨️ Print Receipt</button>
</body>
</html>
  `);

  receiptWindow.document.close();
  receiptWindow.focus();
};
